import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import api from '../../services/api';

const CATEGORIES = ['Medical', 'Daily Care', 'Maintenance', 'Conditioning', 'Stats', 'Travel', 'Notes'];
const CARE_TYPES = {
  'Medical':      ['Vaccination', 'Medication', 'Supplement', 'Procedure', 'Exam', 'Foaling', 'Other'],
  'Daily Care':   ['Feed', 'Water', 'Groom', 'Wraps & Packs', 'Order Supplies / Tack', 'Boarding'],
  'Maintenance':  ['Farrier', 'Dental', 'Chiropractic', 'Body Work / Massage', 'Other'],
  'Conditioning': ['Exercise', 'Training', 'Rehab', 'Other'],
  'Stats':        ['Weight', 'Height', 'Temperature', 'Vitals', 'Other'],
  'Travel':       ['Transport', 'Other'],
  'Notes':        ['General Note'],
};
const PERFORMED_BY_TYPES = ['In-house', 'Vet', 'Farrier', 'Trainer', 'Other'];

export default function CareLogEntryModal({ open, onClose, onSaved, horse, locations, catalogItems, prefill }) {
  const today = new Date().toLocaleDateString('sv');
  const initForm = () => ({
    category: prefill?.category || 'Medical',
    type: prefill?.type || 'Vaccination',
    name: prefill?.name || '',
    dosage: prefill?.dosage || '',
    dosage_unit: prefill?.dosage_unit || '',
    units: prefill?.units ?? '',
    care_catalog_item_id: prefill?.care_catalog_item_id || null,
    log_date: today,
    log_time: '',
    location_id: '',
    performed_by: '',
    performed_by_type: 'In-house',
    comments: '',
    publish_to_owner_portal: false,
    is_breeding: prefill?.is_breeding || false,
  });

  const [form, setForm] = useState(initForm);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open) setForm(initForm());
  }, [open, prefill]);

  const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleCatalogSelect = (itemId) => {
    const item = catalogItems?.find(c => c.id === itemId);
    if (item) {
      setForm(prev => ({
        ...prev,
        care_catalog_item_id: item.id,
        category: item.category,
        type: item.type,
        name: item.name,
        dosage: item.dosage || '',
        dosage_unit: item.dosage_unit || '',
        units: item.units ?? '',
        is_breeding: item.is_breeding || false,
      }));
    }
  };

  const handleSave = async () => {
    if (!form.category || !form.name.trim()) { toast.error('Category and name are required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        horse_id: horse.id,
        units: form.units !== '' ? parseFloat(form.units) : null,
      };
      const res = await api.post('/farmfolio/care-log', payload);
      toast.success('Care logged');
      onSaved(res.data);
      onClose();
    } catch { toast.error('Failed to save care log entry'); }
    setSaving(false);
  };

  const showDosage = form.category === 'Medical';
  const availableTypes = CARE_TYPES[form.category] || [];
  const filteredCatalog = catalogItems?.filter(c => c.category === form.category) || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Log Care</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {catalogItems && catalogItems.length > 0 && (
            <div className="space-y-1">
              <Label>Select from Care Catalog (optional)</Label>
              <Select onValueChange={handleCatalogSelect}>
                <SelectTrigger><SelectValue placeholder="Choose a catalog item…" /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => {
                    const catItems = catalogItems.filter(c => c.category === cat);
                    if (!catItems.length) return null;
                    return (
                      <React.Fragment key={cat}>
                        <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">{cat}</div>
                        {catItems.map(item => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.type} — {item.name}{item.dosage ? ` (${item.dosage} ${item.dosage_unit || ''})`.trim() : ''}
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={v => f('category', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={v => f('type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{availableTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label>Name *</Label>
            <Input value={form.name} onChange={e => f('name', e.target.value)} placeholder="e.g. Rabies Booster, 2 scoops Purina" />
          </div>

          {showDosage && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Dosage</Label>
                <Input value={form.dosage} onChange={e => f('dosage', e.target.value)} placeholder="e.g. 100" />
              </div>
              <div className="space-y-1">
                <Label>Unit</Label>
                <Input value={form.dosage_unit} onChange={e => f('dosage_unit', e.target.value)} placeholder="mL, cc, mg…" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Units / Qty</Label>
              <Input type="number" min="0" value={form.units} onChange={e => f('units', e.target.value)} placeholder="1" />
            </div>
            <div className="space-y-1">
              <Label>Date *</Label>
              <Input type="date" value={form.log_date} onChange={e => f('log_date', e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Time</Label>
              <Input type="time" value={form.log_time} onChange={e => f('log_time', e.target.value)} className={form.log_time ? 'time-filled' : ''} />
            </div>
            <div className="space-y-1">
              <Label>Location</Label>
              <Select value={form.location_id || ''} onValueChange={v => f('location_id', v === '__none__' ? null : v || null)}>
                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {(locations || []).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Performed By</Label>
              <Input value={form.performed_by} onChange={e => f('performed_by', e.target.value)} placeholder="Name" />
            </div>
            <div className="space-y-1">
              <Label>Type</Label>
              <Select value={form.performed_by_type} onValueChange={v => f('performed_by_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PERFORMED_BY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label>Comments</Label>
            <Textarea rows={2} value={form.comments} onChange={e => f('comments', e.target.value)} placeholder="Optional notes" />
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={form.publish_to_owner_portal} onCheckedChange={v => f('publish_to_owner_portal', v)} />
            <Label className="cursor-pointer text-sm">Publish to Owner Portal</Label>
          </div>

        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
            {saving ? 'Saving…' : 'Log Care'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
